/**
 * 分摊信息区块 — 关联航班悬浮提示的第 4 个区块。
 *
 * 展示当前 line_item 的分摊规则及当前悬浮航班的具体分摊详情：
 *  - DIRECT：直接归集金额
 *  - BY_FUEL_QUANTITY（正常）：聚合油量 + 权重百分比 + 分摊金额 + 计算过程
 *  - BY_FUEL_QUANTITY（降级）：橙色告警 + 平均分摊金额
 *  - 其他规则：权重百分比 + 分摊金额（前端按数量平均估算）
 *
 * 分摊明细由上层 FlightValidationCell 通过 buildAllocationBreakdown 在 line_item 级别 memoize，
 * 本组件仅负责纯展示，避免每次悬浮重复计算。
 */
import React from "react";
import { Collapse, Divider, Space, Typography } from "antd";
import { CalculatorOutlined, WarningOutlined } from "@ant-design/icons";
import { ALLOCATION_RULE_LABELS } from "../../../constants";
import { ALLOCATION_DEGRADED_REASONS } from "../../utils/allocationCalculator";
import { resolveProductInfo } from "../../utils/reviewPayloadSemantics";
import { roundMoney } from "../../utils/money";

const { Text } = Typography;

const formatMoney = (value) => {
    const v = roundMoney(value);
    return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatMoneyOrDash = (value) =>
    value === null || value === undefined ? "—" : formatMoney(value);

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const formatQty = (value, unit) => {
    const numStr = Number(roundMoney(value)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return unit ? `${numStr} ${unit}` : numStr;
};

/**
 * 从 productMap 解析产品展示名称（兼容 string 与 {name,cost_type} 两种结构）。
 */
const resolveProductDisplayName = (productMap, code) => {
    if (!code) return null;
    const { productInfo } = resolveProductInfo(productMap, code);
    if (!productInfo) return null;
    return typeof productInfo === "object" ? productInfo.name : productInfo;
};

const SECTION_HEADER_STYLE = { margin: "8px 0" };

// 来源行超过该阈值时折叠，避免浮窗过长
const SOURCE_ITEMS_COLLAPSE_THRESHOLD = 4;

/**
 * 渲染来源行明细（item_id + 数量 × 单价 = 总金额）。
 */
const SourceItemsList = ({ items, unit }) => {
    if (!items || items.length === 0) return null;

    const renderItem = (item) => {
        const qtyStr = formatQty(item.quantity, unit);
        return (
            <div
                key={item.itemId ?? item.quantity}
                style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr auto auto auto",
                    gap: 4,
                    alignItems: "baseline",
                    fontFamily: "monospace",
                    fontSize: 11,
                    lineHeight: 1.7,
                }}
            >
                <Text style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.itemId ?? ""}>
                    {item.itemId ?? "—"}
                </Text>
                <span style={{ textAlign: "right" }}>{qtyStr}</span>
                <span style={{ color: "#8c8c8c" }}>×</span>
                <span style={{ textAlign: "right", minWidth: 52 }}>{formatMoneyOrDash(item.unitPrice)}</span>
                <span style={{ textAlign: "right", fontWeight: 500 }}>{formatMoneyOrDash(item.totalPrice)}</span>
            </div>
        );
    };

    if (items.length <= SOURCE_ITEMS_COLLAPSE_THRESHOLD) {
        return <div style={{ marginTop: 2 }}>{items.map(renderItem)}</div>;
    }

    return (
        <div style={{ marginTop: 2 }}>
            <Collapse
                ghost
                size="small"
                defaultActiveKey={["source-items"]}
                style={{ padding: 0, margin: 0 }}
                items={[
                    {
                        key: "source-items",
                        label: (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                共 {items.length} 行（点击折叠/展开）
                            </Text>
                        ),
                        children: <div>{items.map(renderItem)}</div>,
                    },
                ]}
            />
        </div>
    );
};

/**
 * @param {Object} props
 * @param {Object} props.breakdown       buildAllocationBreakdown 的返回值（已 memoize）
 * @param {string|number} props.flightId 当前悬浮的航班 ID
 * @param {Object} [props.productMap]    product_code -> 名称映射，用于展示权重来源的产品名称
 */
const AllocationInfoBlock = ({ breakdown, flightId, productMap }) => {
    if (!breakdown || breakdown.mode === "NONE") return null;

    const ruleLabel = ALLOCATION_RULE_LABELS[breakdown.rule] || breakdown.rule;

    // ===== DIRECT：直接归集 =====
    if (breakdown.mode === "DIRECT") {
        return (
            <>
                <Divider style={SECTION_HEADER_STYLE}>
                    <Space size={4}>
                        <CalculatorOutlined style={{ color: "#08979c" }} />
                        <Text style={{ fontSize: 12, color: "#08979c" }}>费用分摊</Text>
                    </Space>
                </Divider>
                <div style={{ background: "#e6fffb", border: "1px solid #87e8de", borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.6 }}>
                    <div>
                        <Text type="secondary">规则：</Text>
                        <Text strong>直接归集</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">本航班：</Text>
                        <Text strong style={{ color: "#08979c" }}>
                            直接归集 {formatMoney(breakdown.totalAmount)}
                        </Text>
                    </div>
                </div>
            </>
        );
    }

    // 定位当前悬浮航班的分摊项（类型兼容比较）
    const matched = (breakdown.flightAllocations || []).find((a) => String(a.flightId) === String(flightId));

    // ===== FUEL + 降级 =====
    if (breakdown.mode === "FUEL" && breakdown.degraded) {
        const reasonText = ALLOCATION_DEGRADED_REASONS[breakdown.reason] || breakdown.detail || "未知原因";
        return (
            <>
                <Divider style={SECTION_HEADER_STYLE}>
                    <Space size={4}>
                        <WarningOutlined style={{ color: "#faad14" }} />
                        <Text style={{ fontSize: 12, color: "#faad14" }}>费用分摊</Text>
                    </Space>
                </Divider>
                <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.6 }}>
                    <div style={{ color: "#d48806", fontWeight: 600, marginBottom: 4 }}>
                        <WarningOutlined style={{ marginRight: 4 }} />
                        已降级为按数量平均分摊
                    </div>
                    <div>
                        <Text type="secondary">原始规则：</Text>
                        {ruleLabel}
                    </div>
                    <div>
                        <Text type="secondary">降级原因：</Text>
                        {reasonText}
                    </div>
                    <Divider style={{ margin: "6px 0" }} />
                    <div>
                        <Text type="secondary">本航班：</Text>
                    </div>
                    {matched ? (
                        <div style={{ fontWeight: 600, color: "#faad14", marginTop: 2 }}>
                            {formatPercent(matched.percentage)}
                            {" → "}
                            {formatMoney(matched.amount)}
                        </div>
                    ) : (
                        <Text type="secondary">该航班未参与分摊</Text>
                    )}
                </div>
            </>
        );
    }

    // ===== FUEL 正常 / EVEN（其他规则） =====
    const isFuel = breakdown.mode === "FUEL";
    const accentColor = isFuel ? "#08979c" : "#1890ff";
    const bgColor = isFuel ? "#e6fffb" : "#e6f4ff";
    const borderColor = isFuel ? "#87e8de" : "#91caff";

    return (
        <>
            <Divider style={SECTION_HEADER_STYLE}>
                <Space size={4}>
                    <CalculatorOutlined style={{ color: accentColor }} />
                    <Text style={{ fontSize: 12, color: accentColor }}>费用分摊</Text>
                </Space>
            </Divider>
            <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 10, fontSize: 12, lineHeight: 1.6 }}>
                <div>
                    <Text type="secondary">规则：</Text>
                    <Text strong>{ruleLabel}</Text>
                </div>

                {matched ? (
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">本航班：</Text>
                        <div style={{ fontWeight: 600, color: accentColor, marginTop: 2 }}>
                            {isFuel && matched.aggregatedQuantity != null
                                ? `${formatQty(matched.aggregatedQuantity, breakdown.unit)} (${formatPercent(matched.percentage)})`
                                : formatPercent(matched.percentage)}
                            {" → "}
                            {formatMoney(matched.amount)}
                        </div>
                    </div>
                ) : (
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">本航班：</Text>
                        <Text type="secondary">该航班未参与分摊</Text>
                    </div>
                )}

                {/* BY_FUEL_QUANTITY 计算过程说明 */}
                {isFuel && (
                    <>
                        <Divider style={{ margin: "6px 0" }} />
                        <div>
                            <Text type="secondary">权重来源：</Text>
                            {breakdown.weightSource?.productCodes?.length > 0
                                ? breakdown.weightSource.productCodes
                                      .map((code) => {
                                          const name = resolveProductDisplayName(productMap, code);
                                          return name ? `${name} (${code})` : code;
                                      })
                                      .join(" / ")
                                : "未声明"}
                            {" 行的 "}
                            {breakdown.weightSource?.field || "quantity"}
                            {" 聚合"}
                        </div>
                        {breakdown.totalQuantity != null && breakdown.totalQuantity > 0 && (
                            <div>
                                <Text type="secondary">聚合总量：</Text>
                                {formatQty(breakdown.totalQuantity, breakdown.unit)}
                            </div>
                        )}
                        {breakdown.sourceItems?.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                                <Text type="secondary">来源行：</Text>
                                <SourceItemsList items={breakdown.sourceItems} unit={breakdown.unit} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default AllocationInfoBlock;
